import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, Skeleton } from '@yhelp/ui';
import { useAuth } from '@/auth';
import { useMe } from '@/hooks/useMe';
import { DashboardLayout } from '@/templates/DashboardLayout';
import { useProductTour } from '@/components/organisms/ProductTour/ProductTour';

const ONBOARDING_STEPS = [
  { title: 'Explorar chamados', desc: 'Veja a lista e filtros de status.' },
  { title: 'Abrir um chamado', desc: 'Use o formulário guiado de criação.' },
  { title: 'Conversar no chat', desc: 'Envie mensagens pelo botão flutuante amarelo.' },
] as const;

const MOCK_ACTIVITY = [
  { number: 1042, title: 'Erro ao acessar relatório', status: 'em_andamento' as const },
  { number: 1038, title: 'Novo usuário no sistema', status: 'aberto' as const },
  { number: 1029, title: 'Dúvida sobre SLA', status: 'resolvido' as const },
];

function YhelpIllustration() {
  return (
    <svg viewBox="0 0 120 80" className="mx-auto h-20 w-32" aria-hidden="true">
      <rect x="10" y="20" width="100" height="50" rx="8" fill="#FFE600" />
      <text x="60" y="52" textAnchor="middle" fill="#000" fontSize="18" fontWeight="bold">
        yHelp
      </text>
    </svg>
  );
}

export function OnboardingPage() {
  const { user } = useAuth();
  const { data: me, isLoading } = useMe();
  const tenant = me?.data.tenant;
  const { startTour } = useProductTour();
  const completedSteps = 0;

  return (
    <DashboardLayout pageDescription="Configure sua conta em poucos passos">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <div className="mb-2 flex justify-between text-sm text-muted">
            <span>Progresso</span>
            <span>{completedSteps} de 3 passos</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
            <div
              className="h-full rounded-full bg-yhelp-yellow transition-all duration-slow"
              style={{ width: `${(completedSteps / 3) * 100}%` }}
            />
          </div>
        </div>

        <Card variant="featured" className="p-8">
          <YhelpIllustration />
          {isLoading ? (
            <Skeleton className="mx-auto mt-4 h-8 w-64" />
          ) : (
            <h2 className="mt-4 text-center text-2xl font-extrabold text-yhelp-black dark:text-white">
              Bem-vindo, {user?.name}!
            </h2>
          )}
          <p className="mt-2 text-center text-neutral-600 dark:text-neutral-400">
            Você está conectado à organização <strong>{tenant?.name ?? '—'}</strong> como{' '}
            <strong>{user?.role}</strong>.
          </p>
          <p className="mt-4 text-center text-sm text-muted">
            Recomendamos o tour interativo para aprender na prática:
          </p>
          <ul className="mt-4 space-y-3">
            {ONBOARDING_STEPS.map((item, i) => (
              <li
                key={item.title}
                className="flex gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-950"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yhelp-yellow text-sm font-bold text-yhelp-black">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-yhelp-black dark:text-white">{item.title}</p>
                  <p className="text-sm text-muted">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={() => startTour('welcome')}>Iniciar tour guiado</Button>
            <Link to="/app">
              <Button variant="secondary">Ir direto ao app</Button>
            </Link>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export function AppHomePage() {
  const { user } = useAuth();
  const { data: me, isLoading } = useMe();
  const tenant = me?.data.tenant;
  const { startTour } = useProductTour();

  const cards = [
    {
      title: 'Chamados',
      desc: 'Crie, acompanhe e converse nos seus tickets.',
      to: '/tickets' as const,
      cta: 'Ver chamados',
      icon: '🎫',
    },
    {
      title: 'Tour guiado',
      desc: 'Aprenda na prática com spotlight e passos interativos.',
      action: () => startTour('welcome'),
      cta: 'Iniciar tour',
      icon: '🧭',
    },
    {
      title: 'Base de conhecimento',
      desc: 'Artigos sobre status, SLA e primeiros passos.',
      to: '/kb' as const,
      cta: 'Abrir KB',
      icon: '📚',
    },
    {
      title: 'Notificações',
      desc: 'Alertas sobre chamados, comentários e status.',
      to: '/notifications' as const,
      cta: 'Ver alertas',
      icon: '🔔',
    },
  ];

  return (
    <DashboardLayout pageDescription="Painel principal do yHelp">
      {isLoading ? (
        <Skeleton className="h-32 w-full rounded-2xl" />
      ) : (
        <Card
          variant="accent"
          className="bg-gradient-to-br from-yhelp-yellow/20 via-white to-white p-8 dark:from-yhelp-yellow/10 dark:via-neutral-900 dark:to-neutral-900"
        >
          <h2 className="text-2xl font-extrabold text-yhelp-black dark:text-white">
            Olá, {user?.name}
          </h2>
          <p className="mt-2 text-muted">
            {tenant?.name ?? '—'} — centralize chamados, SLAs e comunicação da sua equipe.
          </p>
        </Card>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card, i) => (
          <Card
            key={card.title}
            variant="interactive"
            className="list-stagger p-6 opacity-0"
            style={{ animationDelay: `${120 + i * 80}ms` }}
          >
            <span className="text-2xl" aria-hidden="true">
              {card.icon}
            </span>
            <h3 className="mt-3 font-bold text-yhelp-black dark:text-white">{card.title}</h3>
            <p className="mt-2 text-sm text-muted">{card.desc}</p>
            <div className="mt-4">
              {'to' in card && card.to ? (
                <Link to={card.to}>
                  <Button size="sm">{card.cta}</Button>
                </Link>
              ) : (
                <Button size="sm" onClick={'action' in card ? card.action : undefined}>
                  {card.cta}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-6 p-6">
        <h3 className="font-bold text-yhelp-black dark:text-white">Atividade recente</h3>
        <ul className="mt-4 divide-y divide-neutral-200 dark:divide-neutral-800">
          {MOCK_ACTIVITY.map((t) => (
            <li key={t.number} className="flex items-center gap-3 py-3">
              <span className="font-mono text-sm text-muted">#{t.number}</span>
              <span className="flex-1 text-sm font-medium text-yhelp-black dark:text-white">
                {t.title}
              </span>
              <Badge variant="status" status={t.status}>
                {t.status.replace('_', ' ')}
              </Badge>
            </li>
          ))}
        </ul>
      </Card>
    </DashboardLayout>
  );
}
